import {
  COMMUNITIES_DIRECTORY_FETCH_REQUEST,
  COMMUNITIES_DIRECTORY_FETCH_SUCCESS,
  COMMUNITIES_DIRECTORY_FETCH_FAIL,
  COMMUNITIES_MINE_FETCH_SUCCESS,
  COMMUNITY_FETCH_SUCCESS,
  COMMUNITY_CREATE_SUCCESS,
  COMMUNITY_UPDATE_SUCCESS,
  COMMUNITY_DELETE_SUCCESS,
  COMMUNITY_JOIN_SUCCESS,
  COMMUNITY_LEAVE_SUCCESS,
  COMMUNITY_TIMELINE_FETCH_REQUEST,
  COMMUNITY_TIMELINE_FETCH_SUCCESS,
  COMMUNITY_TIMELINE_FETCH_FAIL,
  COMMUNITY_MEMBERS_FETCH_SUCCESS,
} from 'mastodon/actions/communities';
import type {
  ApiCommunityJSON,
  ApiCommunityMembershipJSON,
} from 'mastodon/api_types/communities';

interface CommunitiesState {
  directory: ApiCommunityJSON[];
  directoryLoading: boolean;
  directoryError: string | null;

  mine: ApiCommunityJSON[];

  byId: Record<string, ApiCommunityJSON>;

  membersByCommunity: Record<string, ApiCommunityMembershipJSON[]>;

  timelinesByCommunity: Record<
    string,
    {
      statusIds: string[];
      loading: boolean;
      error: string | null;
      hasMore: boolean;
    }
  >;
}

const initialState: CommunitiesState = {
  directory: [],
  directoryLoading: false,
  directoryError: null,

  mine: [],

  byId: {},
  membersByCommunity: {},
  timelinesByCommunity: {},
};

type Action = { type: string; [key: string]: unknown };

const upsertById = (
  byId: CommunitiesState['byId'],
  community: ApiCommunityJSON,
) => ({ ...byId, [community.id]: community });

export const communitiesReducer = (
  state: CommunitiesState = initialState,
  action: Action,
): CommunitiesState => {
  switch (action.type) {
    case COMMUNITIES_DIRECTORY_FETCH_REQUEST:
      return { ...state, directoryLoading: true, directoryError: null };

    case COMMUNITIES_DIRECTORY_FETCH_SUCCESS: {
      const list = action.communities as ApiCommunityJSON[];
      let byId = state.byId;
      list.forEach((c) => {
        byId = upsertById(byId, c);
      });
      return {
        ...state,
        directoryLoading: false,
        directory: list,
        byId,
      };
    }

    case COMMUNITIES_DIRECTORY_FETCH_FAIL:
      return {
        ...state,
        directoryLoading: false,
        directoryError: String(action.error),
      };

    case COMMUNITIES_MINE_FETCH_SUCCESS: {
      const list = action.communities as ApiCommunityJSON[];
      let byId = state.byId;
      list.forEach((c) => {
        byId = upsertById(byId, c);
      });
      return { ...state, mine: list, byId };
    }

    case COMMUNITY_FETCH_SUCCESS: {
      const c = action.community as ApiCommunityJSON;
      return { ...state, byId: upsertById(state.byId, c) };
    }

    case COMMUNITY_CREATE_SUCCESS: {
      const c = action.community as ApiCommunityJSON;
      return {
        ...state,
        byId: upsertById(state.byId, c),
        mine: [c, ...state.mine],
      };
    }

    case COMMUNITY_UPDATE_SUCCESS: {
      const c = action.community as ApiCommunityJSON;
      return {
        ...state,
        byId: upsertById(state.byId, c),
        directory: state.directory.map((d) => (d.id === c.id ? c : d)),
        mine: state.mine.map((d) => (d.id === c.id ? c : d)),
      };
    }

    case COMMUNITY_DELETE_SUCCESS: {
      const id = action.communityId as string;
      const { [id]: _removed, ...byId } = state.byId;
      return {
        ...state,
        byId,
        directory: state.directory.filter((c) => c.id !== id),
        mine: state.mine.filter((c) => c.id !== id),
      };
    }

    case COMMUNITY_JOIN_SUCCESS: {
      const c = action.community as ApiCommunityJSON;
      const inMine = state.mine.some((m) => m.id === c.id);
      return {
        ...state,
        byId: upsertById(state.byId, c),
        mine: inMine
          ? state.mine.map((m) => (m.id === c.id ? c : m))
          : [c, ...state.mine],
      };
    }

    case COMMUNITY_LEAVE_SUCCESS: {
      const id = action.communityId as string;
      const existing = state.byId[id];
      const updated = existing
        ? { ...existing, viewer_role: null, viewer_state: null }
        : existing;
      return {
        ...state,
        byId: updated ? { ...state.byId, [id]: updated } : state.byId,
        mine: state.mine.filter((m) => m.id !== id),
      };
    }

    case COMMUNITY_TIMELINE_FETCH_REQUEST: {
      const id = action.communityId as string;
      return {
        ...state,
        timelinesByCommunity: {
          ...state.timelinesByCommunity,
          [id]: {
            statusIds: state.timelinesByCommunity[id]?.statusIds ?? [],
            loading: true,
            error: null,
            hasMore: state.timelinesByCommunity[id]?.hasMore ?? true,
          },
        },
      };
    }

    case COMMUNITY_TIMELINE_FETCH_SUCCESS: {
      const id = action.communityId as string;
      const incoming = action.statusIds as string[];
      const prev = state.timelinesByCommunity[id]?.statusIds ?? [];
      const merged = action.maxId
        ? Array.from(new Set([...prev, ...incoming]))
        : incoming;
      return {
        ...state,
        timelinesByCommunity: {
          ...state.timelinesByCommunity,
          [id]: {
            statusIds: merged,
            loading: false,
            error: null,
            hasMore: incoming.length >= 20,
          },
        },
      };
    }

    case COMMUNITY_TIMELINE_FETCH_FAIL: {
      const id = action.communityId as string;
      return {
        ...state,
        timelinesByCommunity: {
          ...state.timelinesByCommunity,
          [id]: {
            statusIds: state.timelinesByCommunity[id]?.statusIds ?? [],
            loading: false,
            error: String(action.error),
            hasMore: state.timelinesByCommunity[id]?.hasMore ?? true,
          },
        },
      };
    }

    case COMMUNITY_MEMBERS_FETCH_SUCCESS: {
      const id = action.communityId as string;
      const members = action.members as ApiCommunityMembershipJSON[];
      return {
        ...state,
        membersByCommunity: { ...state.membersByCommunity, [id]: members },
      };
    }

    default:
      return state;
  }
};
